<template>
<div class="st-page">
  <Navigation />
  <PhotoLightbox :src="lightbox.src" :caption="lightbox.caption" @close="closeLightbox" />
  <div class="container" style="padding-top:20px;">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <div><h4 class="st-page-title"><i class="mdi mdi-account-group me-2"></i>Users & Roles</h4></div>
      </div>
      <button class="st-btn st-btn-blue" @click="openAdd"><i class="mdi mdi-plus me-1"></i>Add User</button>
    </div>

    <!-- Category Filter Tabs -->
    <div class="st-tabs">
      <button class="st-tab" :class="{active:filterGroupId===''}" @click="filterGroupId=''">All</button>
      <button class="st-tab" :class="{active:filterGroupId===g.id}" v-for="g in assignableCategories" :key="g.id" @click="filterGroupId=g.id">{{ g.name }}</button>
    </div>

    <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
    <div v-else>
      <div class="st-card">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="st-search-wrap"><i class="mdi mdi-magnify"></i><input class="st-search" v-model="search" placeholder="Search users..." /></div>
          <span style="color:#8BA4C4;font-size:13px;">{{ filteredUsers.length }} users</span>
        </div>
        <div style="overflow-x:auto;" v-if="filteredUsers.length">
          <table class="st-table">
            <thead><tr><th style="width:60px;">Photo</th><th>Name</th><th>Email</th><th>Phone</th><th>Category</th><th>Campus</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              <tr v-for="u in filteredUsers" :key="u.id">
                <td>
                  <div class="um-avatar" @click="u.photo_url ? openLightbox(u) : openEdit(u)" :title="u.photo_url ? 'Click to enlarge' : 'Click to add photo'">
                    <img v-if="u.photo_url" :src="u.photo_url" :alt="u.name" loading="lazy" />
                    <span v-else class="um-avatar-fallback">{{ avatarInitials(u.name) }}</span>
                  </div>
                </td>
                <td style="font-weight:600;">{{ u.name }}</td>
                <td>{{ u.email || '-' }}</td>
                <td>{{ u.phone || '-' }}</td>
                <td>
                  <span v-if="u.permission_group_name" class="st-badge" :class="roleBadge(u.role)">{{ u.permission_group_name }}</span>
                  <span v-else style="font-size:11px;color:#F59E0B;">— unassigned —</span>
                </td>
                <td>{{ u.campus_name || 'All' }}</td>
                <td><span class="st-badge" :class="u.is_active==1?'st-badge-success':'st-badge-danger'">{{ u.is_active==1?'Active':'Inactive' }}</span></td>
                <td>
                  <button class="st-btn st-btn-sm st-btn-blue me-1" @click="openEdit(u)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                  <button class="st-btn st-btn-sm" :class="u.is_active==1?'st-btn-red':'st-btn-green'" @click="toggleActive(u)" :title="u.is_active==1?'Deactivate':'Activate'">
                    <i class="mdi" :class="u.is_active==1?'mdi-account-off':'mdi-account-check'"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="st-empty"><i class="mdi mdi-account-group"></i>No users found</div>
      </div>
    </div>

    <!-- ADD / EDIT MODAL -->
    <div class="st-modal-overlay" v-if="showModal" @click.self="showModal=false">
      <div class="st-modal">
        <div class="st-modal-title">
          <span><i class="mdi me-2" :class="editMode?'mdi-pencil':'mdi-account-plus'"></i>{{ editMode ? 'Edit User' : 'Add User' }}</span>
          <button class="st-modal-close" @click="showModal=false">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="formError">{{ formError }}</div>

        <!-- SUCCESS STATE -->
        <div v-if="createSuccess" class="text-center">
          <div class="st-success-icon"><i class="mdi mdi-check-circle"></i></div>
          <h5 style="color:#E8EDF4;font-weight:700;margin:16px 0 8px;">User Created Successfully!</h5>
          <p style="color:#8BA4C4;font-size:14px;">Login credentials sent via {{ sentVia }}.</p>
          <div class="st-credentials-box">
            <div class="st-cred-row"><span class="st-cred-label">Name</span><span class="st-cred-val">{{ createdUser.name }}</span></div>
            <div class="st-cred-row"><span class="st-cred-label">Category</span><span class="st-cred-val">{{ createdUser.role }}</span></div>
            <div class="st-cred-row" v-if="createdUser.email"><span class="st-cred-label">Email</span><span class="st-cred-val">{{ createdUser.email }}</span></div>
            <div class="st-cred-row" v-if="createdUser.phone"><span class="st-cred-label">Phone</span><span class="st-cred-val">{{ createdUser.phone }}</span></div>
            <div class="st-cred-row"><span class="st-cred-label">Password</span><span class="st-cred-val" style="color:#F59E0B;font-weight:700;">{{ tempPassword }}</span></div>
          </div>
          <div class="d-flex gap-2 mt-4">
            <button class="btn st-btn-primary flex-fill" @click="openAdd"><i class="mdi mdi-plus me-1"></i>Create Another User</button>
            <button class="btn flex-fill" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:12px 20px;" @click="showModal=false;createSuccess=false">Close</button>
          </div>
        </div>

        <!-- EDIT SUCCESS -->
        <div v-else-if="editSuccess" class="text-center">
          <div class="st-success-icon"><i class="mdi mdi-check-circle"></i></div>
          <h5 style="color:#E8EDF4;font-weight:700;margin:16px 0 8px;">User Updated!</h5>
          <button class="btn st-btn-primary mt-3" @click="showModal=false;editSuccess=false">Close</button>
        </div>

        <!-- FORM STATE -->
        <div v-else>
          <!-- Profile photo (edit mode only — needs an existing user id to upload against) -->
          <div v-if="editMode" class="d-flex align-items-center gap-3 mb-3" style="background:rgba(46,134,222,.05);border:1px solid #1A3A6A;border-radius:10px;padding:12px;">
            <div style="width:72px;height:72px;border-radius:50%;overflow:hidden;background:#0A1628;border:2px solid #1A3A6A;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
              <img v-if="form.photoUrl" :src="form.photoUrl + '?v=' + photoVersion" style="width:100%;height:100%;object-fit:cover;" alt="" />
              <span v-else style="color:#4FC3F7;font-weight:700;font-size:20px;">{{ avatarInitials(form.name) }}</span>
            </div>
            <div style="flex:1;">
              <div style="color:#E8EDF4;font-size:13px;font-weight:600;margin-bottom:4px;">Profile Photo</div>
              <div style="color:#8BA4C4;font-size:11px;margin-bottom:6px;">Square crop (1:1) for the user avatar.</div>
              <div class="d-flex gap-2 flex-wrap">
                <button class="btn st-btn-sm st-btn-blue" @click="$refs.userPhotoFile.click()" :disabled="photoUploading">
                  <span v-if="photoUploading" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="mdi mdi-image-outline me-1"></i>{{ form.photoUrl ? 'Replace from file' : 'Upload from file' }}
                </button>
                <button class="btn st-btn-sm" style="background:rgba(168,85,247,.15);color:#A855F7;border:1px solid rgba(168,85,247,.4);" @click="openWebcam" :disabled="photoUploading">
                  <i class="mdi mdi-webcam me-1"></i>Use webcam
                </button>
              </div>
              <input ref="userPhotoFile" type="file" accept="image/*" capture="user" style="display:none;" @change="onUserPhotoSelected" />
            </div>
          </div>

          <div class="mb-3"><label class="st-label">Full Name *</label><input class="st-input" v-model="form.name" placeholder="e.g. Jane Kamau" /></div>
          <div class="row g-3 mb-3">
            <div class="col-6"><label class="st-label">Email</label><input type="email" class="st-input" v-model="form.email" placeholder="jane@school.ac.ke" /></div>
            <div class="col-6"><label class="st-label">Phone</label><input class="st-input" v-model="form.phone" placeholder="0712345678" /></div>
          </div>
          <div class="mb-3">
            <label class="st-label">Category *</label>
            <select class="st-select" v-model="form.permissionGroupId">
              <option value="">Select a category...</option>
              <option v-for="g in assignableCategories" :key="g.id" :value="g.id">{{ g.name }}<span v-if="g.description"> — {{ g.description }}</span></option>
            </select>
            <small v-if="!assignableCategories.length" style="color:#F59E0B;font-size:11px;">No categories available. Create them under Settings → Permission Categories.</small>
          </div>
          <div class="st-role-info" v-if="selectedCategory">
            <i class="mdi mdi-information me-1"></i>
            {{ selectedCategory.description || 'Custom category' }}
          </div>
          <div class="row g-3 mb-3">
            <div class="col-6">
              <label class="st-label">Campus</label>
              <select class="st-select" v-model="form.campusId"><option value="">All Campuses</option><option v-for="c in campuses" :key="c.id" :value="c.id">{{ c.name }}</option></select>
            </div>
            <div class="col-6">
              <label class="st-label">Dining Outlet</label>
              <select class="st-select" v-model="form.outletId"><option value="">None</option><option v-for="o in outlets" :key="o.id" :value="o.id">{{ o.name }}</option></select>
            </div>
          </div>
          <div class="mb-3" v-if="!editMode">
            <label class="st-label">Password * <small style="color:#8BA4C4;">(auto-generated if empty)</small></label>
            <input type="password" class="st-input" v-model="form.password" placeholder="Leave empty to auto-generate" />
          </div>
          <div class="d-flex gap-2">
            <button class="btn st-btn-primary flex-fill" @click="saveUser" :disabled="processing">
              <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
              {{ processing ? 'Saving...' : (editMode ? 'Update User' : 'Create User') }}
            </button>
            <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:12px 20px;" @click="showModal=false">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ WEBCAM CAPTURE MODAL ============ -->
    <div class="st-modal-overlay" v-if="showWebcam" @click.self="closeWebcam">
      <div class="st-modal" style="max-width:560px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-webcam me-2"></i>Capture from Webcam</span>
          <button class="st-modal-close" @click="closeWebcam">&times;</button>
        </div>
        <div v-if="webcamError" class="st-alert st-alert-danger" style="margin-bottom:12px;">{{ webcamError }}</div>
        <div style="background:#000;border-radius:10px;overflow:hidden;display:flex;align-items:center;justify-content:center;min-height:300px;position:relative;">
          <video ref="webcamVideo" autoplay playsinline muted style="width:100%;max-width:480px;display:block;transform:scaleX(-1);"></video>
          <div v-if="webcamReady" style="position:absolute;inset:0;pointer-events:none;display:flex;align-items:center;justify-content:center;">
            <!-- 1:1 framing guide for the round avatar -->
            <div style="width:240px;height:240px;border:2px dashed rgba(79,195,247,.7);border-radius:50%;box-shadow:0 0 0 9999px rgba(0,0,0,.45);"></div>
          </div>
        </div>
        <div style="color:#8BA4C4;font-size:11px;text-align:center;margin-top:8px;">Center the face inside the dashed circle, then capture.</div>
        <div class="d-flex gap-2 mt-3">
          <button class="btn st-btn-primary flex-fill" @click="captureWebcam" :disabled="!webcamReady">
            <i class="mdi mdi-camera me-1"></i>Capture
          </button>
          <button class="btn" v-if="webcamDevices.length > 1" @click="cycleWebcamDevice" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 14px;" title="Switch camera">
            <i class="mdi mdi-camera-switch"></i>
          </button>
          <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeWebcam">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ============ PHOTO CROPPER MODAL ============ -->
    <div class="st-modal-overlay" v-if="showCropper" @click.self="closeCropper">
      <div class="st-modal" style="max-width:720px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-crop me-2"></i>Crop Profile Photo</span>
          <button class="st-modal-close" @click="closeCropper">&times;</button>
        </div>
        <div style="color:#8BA4C4;font-size:12px;margin-bottom:12px;">Drag the box to reposition; use the slider to resize. Avatar uses a 1:1 square crop.</div>

        <div class="d-flex gap-3 flex-wrap" style="justify-content:center;">
          <div ref="cropStage" class="crop-stage" :style="{width: cropDisplay.w + 'px', height: cropDisplay.h + 'px'}"
               @mousedown="onCropDragStart" @touchstart="onCropDragStart">
            <img v-if="cropImageSrc" :src="cropImageSrc" :style="{width: cropDisplay.w + 'px', height: cropDisplay.h + 'px'}" draggable="false" />
            <div class="crop-frame"
                 :style="{left: cropBox.x + 'px', top: cropBox.y + 'px', width: cropBox.w + 'px', height: cropBox.h + 'px'}">
              <div class="crop-frame-grid"></div>
            </div>
          </div>

          <div style="display:flex;flex-direction:column;align-items:center;gap:6px;">
            <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.5px;">AVATAR PREVIEW</div>
            <canvas ref="cropPreview" width="120" height="120" style="border:2px solid #4FC3F7;border-radius:50%;background:#0A1628;display:block;"></canvas>
            <div style="color:#4A6A8A;font-size:10px;">40 × 40 px in lists</div>
          </div>
        </div>

        <div class="mt-3" style="padding:0 8px;">
          <label style="color:#8BA4C4;font-size:11px;font-weight:600;">Frame size</label>
          <input type="range" :min="cropMin" :max="cropMax" v-model.number="cropBox.h" @input="onCropResize" style="width:100%;accent-color:#4FC3F7;" />
        </div>

        <div class="d-flex gap-2 mt-3">
          <button class="btn st-btn-primary flex-fill" @click="acceptCrop" :disabled="photoUploading">
            <span v-if="photoUploading" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-check me-1"></i>{{ photoUploading ? 'Uploading…' : 'Use this photo' }}
          </button>
          <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeCropper" :disabled="photoUploading">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import PhotoLightbox from "@/components/simi-students/PhotoLightbox.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: "UserManagement",
  components: { Navigation, PhotoLightbox },
  data() {
    return {
      loading: true,
      users: [],
      campuses: [],
      outlets: [],
      search: '',
      filterGroupId: '',
      showModal: false,
      editMode: false,
      editId: null,
      processing: false,
      formError: '',
      formSuccess: '',
      tempPassword: '',
      createSuccess: false,
      editSuccess: false,
      createdUser: {},
      sentVia: '',
      form: { name: '', email: '', phone: '', role: '', password: '', campusId: '', outletId: '', permissionGroupId: '', photoUrl: '' },
      // Photo + cropper + webcam state (1:1 square avatar)
      photoUploading: false,
      photoVersion: 0,
      showCropper: false,
      cropImage: null,
      cropImageSrc: '',
      cropDisplay: { w: 0, h: 0, scale: 1 },
      cropBox: { x: 0, y: 0, w: 0, h: 0 },
      cropMin: 60, cropMax: 600,
      cropDrag: null,
      showWebcam: false,
      webcamReady: false,
      webcamError: '',
      webcamStream: null,
      webcamDevices: [],
      webcamDeviceIdx: 0,
      editingUserRef: null,                                  // back-reference to the row in this.users
      lightbox: { src: '', caption: '' },                    // click avatar to zoom
      permissionGroups: [],
      roles: [
        { value: 'campus_manager', label: 'Campus Manager', desc: 'Manages a campus', color: '#8B5CF6' },
        { value: 'class_teacher', label: 'Class Teacher', desc: 'Manages a class, enters grades', color: '#2E86DE' },
        { value: 'dining_manager', label: 'Dining Manager', desc: 'Manages dining outlets & menu', color: '#10B981' },
        { value: 'cashier', label: 'Cashier', desc: 'POS transactions only', color: '#F59E0B' },
        { value: 'bus_manager', label: 'Bus Manager', desc: 'Manages buses & routes', color: '#FB923C' },
        { value: 'bursar', label: 'Bursar', desc: 'Cash out, fees, finance', color: '#EF4444' },
      ]
    };
  },
  computed: {
    filteredUsers() {
      var list = this.users;
      if (this.filterGroupId) list = list.filter(u => String(u.permission_group_id || '') === String(this.filterGroupId));
      if (this.search) {
        var s = this.search.toLowerCase();
        list = list.filter(u => (u.name || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s) || (u.phone || '').includes(s));
      }
      return list;
    },
    // Categories the admin is allowed to assign manually — Parent and Student
    // are auto-assigned by the API when those records are created elsewhere.
    assignableCategories() {
      return this.permissionGroups.filter(g => !['parent','student'].includes((g.default_role || '').toLowerCase()));
    },
    selectedCategory() {
      return this.permissionGroups.find(g => String(g.id) === String(this.form.permissionGroupId));
    }
  },
  mounted() { this.loadUsers(); this.loadCampuses(); this.loadOutlets(); this.loadPermissionGroups(); },
  methods: {
    avatarInitials(name) {
      if (!name) return '?';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    openLightbox(u) {
      if (!u || !u.photo_url) return;
      const role = u.permission_group_name || u.role || '';
      this.lightbox = { src: u.photo_url, caption: u.name + (role ? ` · ${role}` : '') };
    },
    closeLightbox() { this.lightbox = { src: '', caption: '' }; },
    async loadUsers() {
      this.loading = true;
      try {
        var r = await executeJson({ function: 'getUsers' }, simitrack);
        if (r.data.success) this.users = Array.isArray(r.data.data) ? r.data.data : (r.data.data.users || []);
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    async loadCampuses() {
      try {
        var r = await executeJson({ function: 'getCampuses' }, simitrack);
        if (r.data.success) this.campuses = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) {}
    },
    async loadOutlets() {
      try {
        var r = await executeJson({ function: 'getDiningOutlets' }, simitrack);
        if (r.data.success) this.outlets = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) {}
    },
    async loadPermissionGroups() {
      try {
        var r = await executeJson({ function: 'listPermissionGroups' }, simitrack);
        if (r.data.success) this.permissionGroups = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) {}
    },
    roleLabel(role) {
      var found = this.roles.find(r => r.value === role);
      return found ? found.label : role;
    },
    roleBadge(role) {
      var map = { super_admin: 'st-badge-admin', campus_manager: 'st-badge-purple', class_teacher: 'st-badge-info', dining_manager: 'st-badge-success', cashier: 'st-badge-warning', bus_manager: 'st-badge-orange', bursar: 'st-badge-danger' };
      return map[role] || 'st-badge-info';
    },
    roleDescription(role) {
      var descs = {
        campus_manager: 'Can manage campuses, outlets, students, users, attendance, and reports for their assigned campus.',
        class_teacher: 'Can view students in their class, take attendance, enter grades, and log discipline cases.',
        dining_manager: 'Can manage menu items, view POS transactions, and manage dining outlets.',
        cashier: 'Can process POS sales (card tap, cash, STK push) at their assigned outlet.',
        bus_manager: 'Can manage buses, routes, drivers, and view bus attendance logs.',
        bursar: 'Can process cash-out requests, view fee records, and manage financial reports.',
      };
      return descs[role] || '';
    },
    openAdd() {
      this.editMode = false; this.editId = null;
      this.formError = ''; this.formSuccess = ''; this.tempPassword = '';
      this.createSuccess = false; this.editSuccess = false; this.createdUser = {};
      this.form = { name: '', email: '', phone: '', role: '', password: '', campusId: '', outletId: '', permissionGroupId: '' };
      this.showModal = true;
    },
    openEdit(u) {
      this.editMode = true; this.editId = u.id;
      this.formError = ''; this.formSuccess = ''; this.tempPassword = '';
      this.form = { name: u.name, email: u.email || '', phone: u.phone || '', role: u.role, password: '', campusId: u.campus_id || '', outletId: u.outlet_id || '', permissionGroupId: u.permission_group_id || '', photoUrl: u.photo_url || '' };
      this.editingUserRef = u;
      this.showModal = true;
    },

    // ============ WEBCAM ============
    async openWebcam() {
      this.webcamError = ''; this.webcamReady = false; this.showWebcam = true;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        const all = await navigator.mediaDevices.enumerateDevices();
        this.webcamDevices = all.filter(d => d.kind === 'videoinput');
        const trackId = stream.getVideoTracks()[0]?.getSettings()?.deviceId;
        const idx = this.webcamDevices.findIndex(d => d.deviceId === trackId);
        this.webcamDeviceIdx = idx >= 0 ? idx : 0;
        this.attachWebcamStream(stream);
      } catch (e) {
        this.webcamError = 'Could not access camera: ' + (e.message || e.name || 'Permission denied. Allow camera in your browser settings.');
      }
    },
    async cycleWebcamDevice() {
      if (this.webcamDevices.length < 2) return;
      this.webcamDeviceIdx = (this.webcamDeviceIdx + 1) % this.webcamDevices.length;
      const id = this.webcamDevices[this.webcamDeviceIdx].deviceId;
      this.stopWebcamStream();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: id } }, audio: false });
        this.attachWebcamStream(stream);
      } catch (e) { this.webcamError = 'Could not switch camera.'; }
    },
    attachWebcamStream(stream) {
      this.webcamStream = stream;
      this.$nextTick(() => {
        const v = this.$refs.webcamVideo;
        if (!v) return;
        v.srcObject = stream;
        v.onloadedmetadata = () => { this.webcamReady = true; };
      });
    },
    stopWebcamStream() {
      if (this.webcamStream) {
        this.webcamStream.getTracks().forEach(t => { try { t.stop(); } catch(e){} });
        this.webcamStream = null;
      }
      this.webcamReady = false;
    },
    closeWebcam() {
      this.stopWebcamStream();
      this.showWebcam = false;
      this.webcamError = '';
    },
    captureWebcam() {
      const v = this.$refs.webcamVideo;
      if (!v || !v.videoWidth) return;
      const c = document.createElement('canvas');
      c.width = v.videoWidth; c.height = v.videoHeight;
      const ctx = c.getContext('2d');
      ctx.translate(c.width, 0); ctx.scale(-1, 1);
      ctx.drawImage(v, 0, 0, c.width, c.height);
      const src = c.toDataURL('image/jpeg', 0.95);
      const img = new Image();
      img.onload = () => {
        this.cropImage = img; this.cropImageSrc = src;
        this.closeWebcam(); this.openCropperFor(img);
      };
      img.src = src;
    },

    // ============ PHOTO CROPPER (1:1 for avatar) ============
    onUserPhotoSelected(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target.result;
        const img = new Image();
        img.onload = () => { this.cropImage = img; this.cropImageSrc = src; this.openCropperFor(img); };
        img.src = src;
      };
      reader.readAsDataURL(file);
      ev.target.value = '';
    },
    openCropperFor(img) {
      const maxStage = 480;
      const r = Math.min(maxStage / img.naturalWidth, maxStage / img.naturalHeight, 1);
      const dispW = Math.round(img.naturalWidth * r);
      const dispH = Math.round(img.naturalHeight * r);
      this.cropDisplay = { w: dispW, h: dispH, scale: r };

      // Initial crop: 1:1, ~80% of the smaller dimension, centered.
      const fit = Math.round(Math.min(dispW, dispH) * 0.9);
      this.cropBox = {
        x: Math.round((dispW - fit) / 2),
        y: Math.round((dispH - fit) / 2),
        w: fit, h: fit,
      };
      this.cropMin = 60;
      this.cropMax = Math.min(dispW, dispH);
      this.showCropper = true;
      this.$nextTick(() => this.renderCropPreview());
    },
    onCropResize() {
      // 1:1 — width follows height; clamp inside the stage.
      let h = Math.max(this.cropMin, Math.min(this.cropMax, this.cropBox.h));
      let w = h;
      if (w > this.cropDisplay.w) { w = this.cropDisplay.w; h = w; }
      if (h > this.cropDisplay.h) { h = this.cropDisplay.h; w = h; }
      this.cropBox.w = w;
      this.cropBox.h = h;
      if (this.cropBox.x + w > this.cropDisplay.w) this.cropBox.x = this.cropDisplay.w - w;
      if (this.cropBox.y + h > this.cropDisplay.h) this.cropBox.y = this.cropDisplay.h - h;
      if (this.cropBox.x < 0) this.cropBox.x = 0;
      if (this.cropBox.y < 0) this.cropBox.y = 0;
      this.renderCropPreview();
    },
    onCropDragStart(e) {
      const stage = this.$refs.cropStage;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const pt = this.cropPoint(e, rect);
      if (pt.x < this.cropBox.x || pt.x > this.cropBox.x + this.cropBox.w
          || pt.y < this.cropBox.y || pt.y > this.cropBox.y + this.cropBox.h) return;
      this.cropDrag = { dx: pt.x - this.cropBox.x, dy: pt.y - this.cropBox.y };
      window.addEventListener('mousemove', this.onCropDragMove);
      window.addEventListener('mouseup', this.onCropDragEnd);
      window.addEventListener('touchmove', this.onCropDragMove, { passive: false });
      window.addEventListener('touchend', this.onCropDragEnd);
      e.preventDefault();
    },
    onCropDragMove(e) {
      if (!this.cropDrag) return;
      const stage = this.$refs.cropStage;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const pt = this.cropPoint(e, rect);
      let nx = pt.x - this.cropDrag.dx;
      let ny = pt.y - this.cropDrag.dy;
      nx = Math.max(0, Math.min(this.cropDisplay.w - this.cropBox.w, nx));
      ny = Math.max(0, Math.min(this.cropDisplay.h - this.cropBox.h, ny));
      this.cropBox.x = nx; this.cropBox.y = ny;
      this.renderCropPreview();
      if (e.cancelable) e.preventDefault();
    },
    onCropDragEnd() {
      this.cropDrag = null;
      window.removeEventListener('mousemove', this.onCropDragMove);
      window.removeEventListener('mouseup', this.onCropDragEnd);
      window.removeEventListener('touchmove', this.onCropDragMove);
      window.removeEventListener('touchend', this.onCropDragEnd);
    },
    cropPoint(e, rect) {
      const t = (e.touches && e.touches[0]) || e;
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    },
    renderCropPreview() {
      const c = this.$refs.cropPreview;
      if (!c || !this.cropImage) return;
      const ctx = c.getContext('2d');
      const s = 1 / this.cropDisplay.scale;
      ctx.fillStyle = '#0A1628';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(this.cropImage,
        this.cropBox.x * s, this.cropBox.y * s, this.cropBox.w * s, this.cropBox.h * s,
        0, 0, c.width, c.height);
    },
    closeCropper() {
      if (this.photoUploading) return;
      this.onCropDragEnd();
      this.showCropper = false;
      this.cropImage = null;
      this.cropImageSrc = '';
    },
    async acceptCrop() {
      if (!this.cropImage || !this.editId) return;
      this.photoUploading = true;
      try {
        // 600×600 JPEG q=0.85 — same compression style as the student photo flow.
        const out = document.createElement('canvas');
        out.width = 600; out.height = 600;
        const octx = out.getContext('2d');
        const s = 1 / this.cropDisplay.scale;
        octx.fillStyle = '#FFFFFF';
        octx.fillRect(0, 0, out.width, out.height);
        octx.drawImage(this.cropImage,
          this.cropBox.x * s, this.cropBox.y * s, this.cropBox.w * s, this.cropBox.h * s,
          0, 0, out.width, out.height);
        const base64 = out.toDataURL('image/jpeg', 0.85).split(',')[1];

        const r = await executeJson({ function: 'uploadUserPhoto', userId: this.editId, photoBase64: base64 }, simitrack);
        if (r.data.success) {
          const url = r.data.data && r.data.data.photo_url ? r.data.data.photo_url : '';
          this.form.photoUrl = url;
          this.photoVersion++;
          if (this.editingUserRef) this.editingUserRef.photo_url = url;
          this.formSuccess = `Photo updated (${r.data.data && r.data.data.file_size_kb ? r.data.data.file_size_kb : '?'}KB)`;
          this.closeCropper();
          setTimeout(() => { this.formSuccess = ''; }, 4000);
        } else {
          alert('✗ ' + (r.data.message || 'Upload failed'));
        }
      } catch (e) {
        alert('✗ Upload error');
      }
      this.photoUploading = false;
    },
    async saveUser() {
      this.formError = ''; this.formSuccess = ''; this.tempPassword = '';
      if (!this.form.name) { this.formError = 'Name is required'; return; }
      if (!this.form.permissionGroupId && !this.editMode) { this.formError = 'Category is required'; return; }
      this.processing = true;
      try {
        var params;
        if (this.editMode) {
          params = { function: 'updateUser', userId: this.editId, ...this.form };
        } else {
          params = { function: 'createUser', ...this.form };
        }
        var r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.loadUsers();
          if (!this.editMode) {
            this.tempPassword = (r.data.data && r.data.data.tempPassword) ? r.data.data.tempPassword : this.form.password;
            var cat = this.permissionGroups.find(g => String(g.id) === String(this.form.permissionGroupId));
            this.createdUser = { name: this.form.name, email: this.form.email, phone: this.form.phone, role: cat ? cat.name : '' };
            this.sentVia = (this.form.phone && this.form.email) ? 'SMS and email' : (this.form.phone ? 'SMS' : 'email');
            this.createSuccess = true;
          } else {
            this.editSuccess = true;
          }
        } else { this.formError = r.data.message || 'Failed'; }
      } catch (e) { this.formError = 'Connection error'; }
      this.processing = false;
    },
    async toggleActive(u) {
      var action = u.is_active == 1 ? 'deactivate' : 'activate';
      if (!confirm(action.charAt(0).toUpperCase() + action.slice(1) + ' user "' + u.name + '"?')) return;
      try {
        var r = await executeJson({ function: 'updateUser', userId: u.id, isActive: u.is_active == 1 ? 0 : 1 }, simitrack);
        if (r.data.success) this.loadUsers();
        else alert(r.data.message || 'Failed');
      } catch (e) { alert('Connection error'); }
    }
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-tabs{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:20px;}
.st-tab{background:#0F2347;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:50px;padding:7px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-tab.active,.st-tab:hover{background:rgba(46,134,222,.15);border-color:#2E86DE;color:#4FC3F7;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:24px;margin-bottom:16px;}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;}
.st-table td{color:#E8EDF4;font-size:13px;padding:12px;border-bottom:1px solid #1A3A6A;}
.st-table tr:hover td{background:rgba(46,134,222,.05);}
.st-badge{padding:3px 10px;border-radius:50px;font-size:11px;font-weight:600;}
.st-badge-success{background:rgba(16,185,129,.15);color:#10B981;}
.st-badge-warning{background:rgba(245,158,11,.15);color:#F59E0B;}
.st-badge-danger{background:rgba(239,68,68,.15);color:#EF4444;}
.st-badge-info{background:rgba(46,134,222,.15);color:#4FC3F7;}
.st-badge-purple{background:rgba(139,92,246,.15);color:#8B5CF6;}
.st-badge-orange{background:rgba(251,146,60,.15);color:#FB923C;}
.st-badge-admin{background:rgba(244,63,94,.15);color:#F43F5E;}
.st-btn{border:none;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-btn-sm{padding:5px 12px;font-size:12px;}
.st-btn-blue{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.3);}
.st-btn-blue:hover{background:rgba(46,134,222,.25);}
.st-btn-red{background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.3);}
.st-btn-green{background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.3);}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:10px;padding:12px;font-size:15px;}
.st-btn-primary:disabled{opacity:.6;}
.st-btn-outline{background:transparent;border-radius:10px;font-size:14px;font-weight:500;}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;}.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-search-wrap{position:relative;}.st-search-wrap i{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#8BA4C4;}
.st-search{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:9px 14px 9px 36px;color:#E8EDF4;font-size:14px;outline:none;width:100%;max-width:300px;}
.st-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
.st-modal{background:#0F2347;border:1px solid #1A3A6A;border-radius:16px;padding:28px;width:100%;max-width:500px;max-height:90vh;overflow-y:auto;}
.st-modal-title{color:#E8EDF4;font-size:18px;font-weight:700;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;}
.st-modal-close{color:#8BA4C4;cursor:pointer;font-size:22px;background:none;border:none;}
.st-label{color:#8BA4C4;font-size:13px;font-weight:600;margin-bottom:6px;display:block;}
.st-input,.st-select{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;transition:border .2s;}
.st-input:focus,.st-select:focus{border-color:#2E86DE;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:16px;}
.st-alert-danger{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.2);color:#FF9999;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#6EE7B7;}
.st-role-info{background:rgba(46,134,222,.08);border:1px solid rgba(46,134,222,.15);border-radius:8px;padding:10px 14px;color:#8BA4C4;font-size:12px;margin-bottom:16px;line-height:1.5;}
.st-success-icon{width:72px;height:72px;background:rgba(16,185,129,.12);border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto;}
.st-success-icon i{font-size:40px;color:#10B981;}
.st-credentials-box{background:#0A1628;border:1px solid #1A3A6A;border-radius:12px;padding:16px;margin-top:16px;text-align:left;}
.st-cred-row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #1A3A6A;}
.st-cred-row:last-child{border-bottom:none;}
.st-cred-label{color:#8BA4C4;font-size:13px;}
.st-cred-val{color:#E8EDF4;font-size:13px;font-weight:600;}

/* User row avatar */
.um-avatar{width:40px;height:40px;border-radius:50%;overflow:hidden;background:#0F2347;border:1px solid #1A3A6A;display:flex;align-items:center;justify-content:center;flex-shrink:0;cursor:pointer;transition:.15s;}
.um-avatar:hover{border-color:#4FC3F7;transform:scale(1.05);}
.um-avatar img{width:100%;height:100%;object-fit:cover;display:block;}
.um-avatar-fallback{color:#4FC3F7;font-weight:700;font-size:13px;letter-spacing:.5px;}

/* Photo cropper */
.crop-stage{position:relative;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;overflow:hidden;cursor:grab;user-select:none;flex-shrink:0;max-width:100%;}
.crop-stage img{display:block;pointer-events:none;}
.crop-frame{position:absolute;border:2px solid #4FC3F7;cursor:move;
  box-shadow:0 0 0 9999px rgba(10,22,40,.72), 0 0 0 1px rgba(0,0,0,.6) inset;
}
.crop-frame-grid{position:absolute;inset:0;pointer-events:none;
  background:
    linear-gradient(to right, transparent 33.3%, rgba(255,255,255,.3) 33.3%, rgba(255,255,255,.3) 33.5%, transparent 33.5%, transparent 66.6%, rgba(255,255,255,.3) 66.6%, rgba(255,255,255,.3) 66.8%, transparent 66.8%),
    linear-gradient(to bottom, transparent 33.3%, rgba(255,255,255,.3) 33.3%, rgba(255,255,255,.3) 33.5%, transparent 33.5%, transparent 66.6%, rgba(255,255,255,.3) 66.6%, rgba(255,255,255,.3) 66.8%, transparent 66.8%);
}
</style>
